import { Link } from "react-router-dom";
import { LegalLayout } from "./LegalLayout";

const LINK = "text-primary hover:underline";

/**
 * Terms of Service page.
 *
 * Plain-language terms for what Spark actually does.
 */
export function TermsOfServicePage() {
  return (
    <LegalLayout
      title="Terms of Service"
      meta={
        <>
          <strong>Last Updated:</strong> 29 October 2026
        </>
      }
    >
      <p>
        These Terms of Service (&ldquo;Terms&rdquo;) are an agreement between
        you and Charisol (&ldquo;we,&rdquo; &ldquo;us&rdquo;), which operates
        Spark by Charisol (&ldquo;Spark,&rdquo; &ldquo;the Service&rdquo;) at{" "}
        <a href="https://spark.charisol.io" className={LINK}>
          spark.charisol.io
        </a>
        . By creating an account or using Spark you agree to these Terms and to
        our{" "}
        <Link to="/privacy" className={LINK}>
          Privacy Policy
        </Link>
        . If you do not agree, do not use the Service.
      </p>

      <h2>1. What Spark Is</h2>
      <p>
        Spark helps you turn your experiences, notes and opinions into posts
        written in your own voice, and publish them to the social platforms you
        connect. It uses AI to draft and refine text; you choose what is
        published.
      </p>

      <h2>2. Accounts</h2>
      <ul>
        <li>
          You must be at least 16 years old, and able to enter into a binding
          agreement, to use Spark.
        </li>
        <li>
          Give us accurate information when you register and keep it up to date.
        </li>
        <li>
          You are responsible for your password and for everything done through
          your account. Tell us promptly at{" "}
          <a href="mailto:info@charisol.io" className={LINK}>
            info@charisol.io
          </a>{" "}
          if you think someone else has access to it.
        </li>
        <li>
          Each person may hold one account. Sharing your login details with
          anyone else is not allowed. On Team plans, each seat is for a named
          individual.
        </li>
      </ul>

      <h2>3. Acceptable Use</h2>
      <p>You agree not to use Spark to:</p>
      <ul>
        <li>
          break the law, or post content that is unlawful, defamatory,
          harassing, hateful, sexually exploitative, or that promotes violence
          or self-harm;
        </li>
        <li>
          impersonate another person or organization, or present AI-generated
          content as coming from a real person who has not agreed to it;
        </li>
        <li>
          send spam, run deceptive or coordinated inauthentic campaigns, or
          create fake accounts or engagement;
        </li>
        <li>
          infringe anyone&rsquo;s intellectual property or privacy rights;
        </li>
        <li>
          upload malware, or probe, scrape, overload or attempt to break into
          the Service or other users&rsquo; accounts;
        </li>
        <li>
          resell or give access to the Service to others except as your plan
          allows, or use it to build a competing product by copying it;
        </li>
        <li>
          import material you are not allowed to use. When you bring in
          third-party content, such as meeting transcripts or other
          people&rsquo;s writing, you confirm that you may import it and use it
          to create your own posts;
        </li>
        <li>
          work around plan limits, credits, rate limits or other technical
          restrictions.
        </li>
      </ul>
      <p>
        We may remove content or suspend an account that breaks these rules, and
        we may report unlawful activity to the authorities.
      </p>

      <h2>4. Your Content</h2>
      <ul>
        <li>
          <strong>You own your content.</strong> That includes the source
          material you give Spark and the drafts and posts you create with it.
          We do not claim ownership.
        </li>
        <li>
          <strong>Our licence is limited.</strong> You give Charisol a
          non-exclusive, worldwide licence to host, process and display your
          content, and to send it to the AI providers and social platforms you
          use through Spark, only as needed to provide the Service to you. It
          ends when you delete the content or your account, subject to the
          retention described in the Privacy Policy.
        </li>
        <li>
          <strong>What you publish publicly.</strong> If you publish something
          to other Spark users (for example a voice in the voice gallery), you
          give them the right to use it as the feature allows, and you confirm
          you have the right to publish it. You can unpublish it later.
        </li>
        <li>
          You are responsible for your content. You confirm you have the rights
          to the material you submit and that it follows these Terms.
        </li>
      </ul>

      <h3>Staff access</h3>
      <p>
        Our staff can see metadata about your drafts and posts. They can read a
        draft&rsquo;s text only after recording a reason, such as your support
        request, for a limited time, and each access is logged. They never see
        your Layers or Chat. Details are in the{" "}
        <Link to="/privacy#staff" className={LINK}>
          Privacy Policy
        </Link>
        .
      </p>

      <h2>5. AI-Generated Content</h2>
      <p>
        Spark uses third-party AI models to generate text. AI output can be
        inaccurate, out of date, biased, or similar to existing material, and it
        is not professional advice. You are responsible for reading,
        fact-checking and editing anything before you publish it, and for what
        you publish. Spark does not publish anything without your action. We
        cannot promise that output will be unique or suitable for your purpose.
      </p>

      <h2>6. Third-Party Platforms</h2>
      <ul>
        <li>
          Spark connects to LinkedIn, X and, as they become available, Meta
          platforms (Facebook, Instagram and Threads). Those platforms are not
          run by us, and your use of them is governed by their own terms and
          policies. You must follow them, including their rules on automation,
          advertising disclosure and prohibited content.
        </li>
        <li>
          Spark posts only what you ask it to: when you publish, or at the time
          you schedule. It does not post, like, comment, follow or message on
          your behalf in any other way.
        </li>
        <li>
          A platform can change its API, limits or rules, or suspend your
          account there, and that can affect what Spark can do. We are not
          responsible for those decisions.
        </li>
        <li>
          You can disconnect a platform at any time in Settings &rarr;
          Connections. See the Privacy Policy for what we access and store.
        </li>
        <li>
          LinkedIn, X, Facebook, Instagram and Threads are trademarks of their
          owners. Spark is not endorsed by or affiliated with them.
        </li>
      </ul>

      <h2>7. Plans, Credits and Payment</h2>
      <ul>
        <li>
          Spark is offered on a Free plan and paid plans, described on our{" "}
          <a href="/pricing" className={LINK}>
            pricing page
          </a>
          . Features, limits and monthly credits depend on your plan.
        </li>
        <li>
          Credits are used up as you use AI features. The credits included in
          your plan reset at the start of each billing period; unused plan
          credits do not carry over. Credits you buy separately never expire.
        </li>
        <li>
          Paid plans are billed monthly or annually, in advance, and renew
          automatically until cancelled. Taxes are added where they apply, and
          the currency is shown at checkout.
        </li>
        <li>
          Payments are handled by a payment processor such as Stripe. Your card
          details never touch Spark&rsquo;s servers.
        </li>
        <li>
          <strong>Cancellation and refunds:</strong> You can cancel at any time.
          Your paid access continues until the end of the period you have paid
          for, and nothing is charged after you cancel. We do not give refunds
          for payments already made, and credits you have bought are
          non-refundable.
        </li>
        <li>
          We will give you 30 days&rsquo; notice before any change to prices
          takes effect.
        </li>
      </ul>

      <h3>&ldquo;Distributed by Spark&rdquo;</h3>
      <ul>
        <li>
          This is optional and off unless you turn it on in Settings &rarr;
          Preferences. You can turn it off at any time.
        </li>
        <li>
          When it is on, after a post of yours goes live on LinkedIn, Spark
          posts one comment on it from your account: &ldquo;Distributed by
          Spark&rdquo; with a tracked link to Spark. Settings shows the exact
          text.
        </li>
        <li>
          Each comment that is posted earns you 5 credits. If a comment fails,
          no credits are given for that post and Spark does not try again.
        </li>
        <li>
          We may change or end the deal, giving you notice where practical.
        </li>
      </ul>

      <h2>8. Termination</h2>
      <ul>
        <li>
          You can stop using Spark and delete your account at any time in
          Settings &rarr; Account. Deletion is immediate and cannot be undone;
          see our{" "}
          <Link to="/data-deletion" className={LINK}>
            data deletion instructions
          </Link>
          .
        </li>
        <li>
          We may suspend or close your account if you break these Terms, put the
          Service or other users at risk, or fail to pay, and we will tell you
          why where we reasonably can. We may also end the Service or a feature
          of it, giving notice where practical.
        </li>
        <li>
          Sections that by their nature should continue (content licence limits,
          disclaimers, liability, disputes) survive termination.
        </li>
      </ul>

      <h2>9. Disclaimers</h2>
      <p>
        Spark is provided &ldquo;as is&rdquo; and &ldquo;as available.&rdquo; To
        the extent the law allows, we do not promise that the Service will be
        uninterrupted or error-free, that posts will always publish or schedule
        successfully, or that any result (reach, engagement, growth) will be
        achieved.
      </p>

      <h2>10. Limitation of Liability</h2>
      <p>
        To the extent the law allows, Charisol is not liable for indirect,
        incidental or consequential losses, or for lost profits, revenue, data
        or goodwill, arising from your use of the Service. Our total liability
        for any claim relating to the Service is limited to the amount you paid
        us in the 12 months before the claim arose. Nothing in these Terms
        limits liability that cannot be limited by law.
      </p>

      <h2>11. Disputes</h2>
      <p>
        If you have a dispute with us, please contact us first at{" "}
        <a href="mailto:info@charisol.io" className={LINK}>
          info@charisol.io
        </a>
        . We will try to resolve any dispute with you in good faith before
        taking it further. These Terms are governed by the laws of the Federal
        Republic of Nigeria, and the courts of Nigeria have jurisdiction, but
        this does not take away rights you have under the consumer protection
        laws of the country where you live.
      </p>

      <h2>12. Changes to These Terms</h2>
      <p>
        We may update these Terms. We will change the &ldquo;Last Updated&rdquo;
        date above and, for material changes, tell you by email or in the
        Service at least 30 days before they take effect. Continuing to use
        Spark after a change takes effect means you accept the updated Terms.
      </p>

      <h2>13. Contact</h2>
      <ul>
        <li>
          <strong>Email:</strong>{" "}
          <a href="mailto:info@charisol.io" className={LINK}>
            info@charisol.io
          </a>
        </li>
        <li>
          <strong>Company:</strong> Charisol, a company registered in Nigeria
        </li>
        <li>
          <strong>Privacy:</strong>{" "}
          <a href="mailto:privacy@charisol.io" className={LINK}>
            privacy@charisol.io
          </a>
        </li>
      </ul>
    </LegalLayout>
  );
}
